import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useStoreProducts } from '../hooks/useStoreProducts';
import { useTaxonomy, slugify, useCollectionMeta } from '../hooks/useTaxonomy';
import useReveal from '../hooks/useReveal';

const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`reveal ${visible ? 'in' : ''} ${className}`}
    >
      {children}
    </div>
  );
};

const RoomTile = ({ to, name, image, blurb, comingSoon, count, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group relative block overflow-hidden bg-[#EDE3CF] aspect-[4/5] md:aspect-[3/4]"
  >
    {image ? (
      <img
        src={image}
        alt={name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.06]"
      />
    ) : (
      <div className="absolute inset-0 bg-gradient-to-br from-[#E3D5B8] to-[#C9B596]" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#1E140B]/85 via-[#1E140B]/15 to-transparent" />
    {comingSoon && (
      <div className="absolute top-5 left-5 z-10">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F5EDDD]/95 text-[#33200F] text-[10px] tracking-[0.22em] uppercase">
          <Sparkles className="h-3 w-3" strokeWidth={1.5} /> Coming soon
        </span>
      </div>
    )}
    <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-[#F5EDDD]">
      <p className="text-[10px] tracking-[0.32em] uppercase text-[#E8DBC4]/85 mb-2">
        — By room
        {!comingSoon && count > 0 && (
          <span className="ml-3 text-[#F5EDDD]/70">· {count} {count === 1 ? 'piece' : 'pieces'}</span>
        )}
      </p>
      <h3
        className="text-3xl md:text-4xl leading-[1.05] mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {name}
      </h3>
      {blurb && (
        <p className="text-[13px] text-[#E8DBC4]/90 max-w-md line-clamp-2 mb-4">{blurb}</p>
      )}
      <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#F5EDDD] link-underline">
        {comingSoon ? 'Join the waitlist' : 'Explore'}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

const ROOM_MAINS = ['Bedding', 'Living', 'Dining'];

/**
 * ShopByRoom — three-tile grid (Bedding · Living · Dining) used on the Home
 * page and Collections landing. Pulls live taxonomy + admin-uploaded covers.
 */
const ShopByRoom = ({ eyebrow = 'By room', title = 'Shop by room.', subtitle = 'The room you live in, dressed for it.', className = '' }) => {
  const { main } = useTaxonomy();
  const { map: metaMap } = useCollectionMeta();
  const { products } = useStoreProducts();

  const tiles = useMemo(() => main.filter((m) => ROOM_MAINS.includes(m)).map((m) => {
    const slug = slugify(m);
    const meta = metaMap[slug] || {};
    const fallback = products.find((p) => (p.main_collections || []).includes(m))?.media?.primary_image;
    const count = products.filter((p) => (p.main_collections || []).includes(m)).length;
    return {
      slug,
      name: m,
      to: `/collections/${slug}`,
      image: meta.cover_image_url || fallback,
      blurb: meta.description || `Curated ${m.toLowerCase()} pieces, made in Australia.`,
      comingSoon: count === 0,
      count,
      testId: `room-tile-${slug}`,
    };
  }), [main, products, metaMap]);

  if (tiles.length === 0) return null;

  return (
    <section className={`bg-[#F5EDDD] grain py-12 md:py-16 ${className}`} data-testid="shop-by-room">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="mb-8 md:mb-10 max-w-2xl">
            <p className="text-[11px] tracking-[0.32em] uppercase text-[#A07A4F] mb-3">— {eyebrow}</p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl text-[#33200F] leading-[1.05]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {title} <span className="italic text-[#5B3A24]">{subtitle}</span>
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {tiles.map((c, i) => (
            <Reveal key={c.slug} delay={i * 80}>
              <RoomTile {...c} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ShopByRoom;
